import { useMemo, useState } from 'react';
import type { ProfileTabAction } from '@wlb/shared/constants/profileTabs';
import useUserPosts from '@wlb/shared/hooks/useUserPosts';
import type { FeedPost } from '@wlb/shared/types/post';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import { Button } from '../../components/ui/Button';
import { DataTable, type DataTableColumn } from '../../components/ui/DataTable';
import { IconButton } from '../../components/ui/IconButton';
import { SelectField } from '../../components/ui/SelectField';
import { StateBlock } from '../../components/ui/StateBlock';
import { AdminPostCard } from '../posts/AdminPostCard';
import { formatCount, formatDateTime } from '../../utils/format';
import { useUserDetail } from './userForm';
import '../../components/ui/ui.css';
import '../posts/post-card.css';
import '../../pages/pages.css';

const POST_FILTERS: readonly { action: ProfileTabAction; key: string }[] = [
  { action: 'All', key: 'all' },
  { action: 'Text', key: 'text' },
  { action: 'Photos', key: 'photos' },
  { action: 'Videos', key: 'videos' },
  { action: 'Interacted', key: 'interacted' },
];

/** Feed by default: a post is judged as published, the table is for scanning. */
type ViewMode = 'feed' | 'table';

/** The member's own feed, the sub-module the buddy list links straight into. */
export function UserPostsTab() {
  const { t } = useTranslation('admin');
  const { user } = useUserDetail();
  const [postAction, setPostAction] = useState<ProfileTabAction>('All');
  const [viewMode, setViewMode] = useState<ViewMode>('feed');

  const {
    posts,
    loading,
    loaded,
    error,
    hasNextPage,
    fetchNextPage,
    refresh,
  } = useUserPosts(user.userId, postAction, user.userGuid);

  const columns = useMemo<readonly DataTableColumn<FeedPost>[]>(
    () => [
      {
        key: 'post',
        header: t('users.details.posts.columns.post'),
        render: post => (
          <span className="ui-table__truncate">
            {post.text || post.title || t('common.none')}
          </span>
        ),
      },
      {
        key: 'type',
        header: t('users.details.posts.columns.type'),
        render: post =>
          post.isBlog
            ? t('posts.type.blog')
            : post.groupName
              ? t('posts.type.group')
              : t('posts.type.post'),
      },
      {
        key: 'posted',
        header: t('users.details.posts.columns.posted'),
        render: post => formatDateTime(post.postDate) || post.timeAgo || t('common.none'),
      },
      {
        key: 'engagement',
        header: t('users.details.posts.columns.engagement'),
        render: post =>
          t('users.details.posts.engagement', {
            reactions: formatCount(post.likes),
            comments: formatCount(post.comments),
            shares: formatCount(post.shares),
          }),
      },
    ],
    [t],
  );

  // A filter change refetches behind the posts already listed: only the very
  // first response for this member can legitimately show the placeholder.
  const firstLoad = !loaded && loading;

  const loadMore = hasNextPage ? (
    <Button variant="secondary" small disabled={loading} onClick={fetchNextPage}>
      {loading ? t('common.loading') : t('common.loadMore')}
    </Button>
  ) : null;

  return (
    <div className="page__section">
      <section className="page__toolbar" aria-label={t('common.filters')}>
        <div className="page__toolbar-field">
          <SelectField
            label={t('users.details.posts.filterLabel')}
            options={POST_FILTERS.map(filter => ({
              value: filter.action,
              label: t(`users.details.posts.filters.${filter.key}`),
            }))}
            value={postAction}
            onChange={event =>
              setPostAction(
                POST_FILTERS.find(filter => filter.action === event.target.value)
                  ?.action ?? 'All',
              )
            }
          />
        </div>
        <div className="page__toolbar-field">
          <div className="ui-field">
            <span className="ui-field__label">{t('posts.viewLabel')}</span>
            <div className="ui-view-toggle">
              <button
                type="button"
                className={
                  viewMode === 'feed'
                    ? 'ui-view-toggle__btn ui-view-toggle__btn--active'
                    : 'ui-view-toggle__btn'
                }
                aria-pressed={viewMode === 'feed'}
                onClick={() => setViewMode('feed')}>
                {t('posts.viewCards')}
              </button>
              <button
                type="button"
                className={
                  viewMode === 'table'
                    ? 'ui-view-toggle__btn ui-view-toggle__btn--active'
                    : 'ui-view-toggle__btn'
                }
                aria-pressed={viewMode === 'table'}
                onClick={() => setViewMode('table')}>
                {t('posts.viewTable')}
              </button>
            </div>
          </div>
        </div>
        <div className="page__toolbar-actions">
          <IconButton
            icon="refresh"
            tone="slate"
            label={t('common.refresh')}
            disabled={loading}
            onClick={refresh}
          />
        </div>
      </section>

      {viewMode === 'table' ? (
        <DataTable
          caption={t('users.details.sections.posts')}
          columns={columns}
          rows={posts}
          rowKey={post => post.id}
          status={error ? 'error' : firstLoad ? 'loading' : 'ready'}
          refreshing={loading}
          error={error}
          onRetry={refresh}
          emptyTitle={t('users.details.posts.emptyTitle')}
          emptyMessage={t('users.details.posts.emptyMessage')}
          footer={loadMore ? <div className="ui-pagination">{loadMore}</div> : null}
        />
      ) : (
        <div className="page__feed">
          {error ? (
            <div className="ui-table-panel">
              <StateBlock status="error" message={error} onRetry={refresh} />
            </div>
          ) : firstLoad ? (
            <div className="ui-table-panel">
              <StateBlock status="loading" />
            </div>
          ) : posts.length === 0 ? (
            <div className="ui-table-panel">
              <StateBlock
                status="empty"
                title={t('users.details.posts.emptyTitle')}
                message={t('users.details.posts.emptyMessage')}
              />
            </div>
          ) : (
            <div className="admin-post-list" aria-busy={loading || undefined}>
              {posts.map(post => (
                <AdminPostCard key={post.id} post={post} />
              ))}
              {loadMore ? <div className="admin-post-list__more">{loadMore}</div> : null}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
